import { createSelector } from "@reduxjs/toolkit";
import moment from "moment";
import { getCalendarEvents } from "./WhatsAppSelectors";
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { Campaign } from "../models/campaign-model";
import { campaignsSelectOneObjectByQuery, userMessagesSelectOneObjectByQuery } from "./DBServiceThunks";
import { getCalendarEventColor } from "@common/utils/calendar-colors";
import { CommonRootState } from "@common/types/common-root-state-type";

export const getCalendarEventById = createSelector(
    [
        (state: CommonRootState, eventId, eventDueDate: string, isCampaign: boolean) => state.DBUserMessageReducer,
        (state: CommonRootState, eventId, eventDueDate: string, isCampaign: boolean) => state.DBCampaignReducer,
        (state: CommonRootState, eventId, eventDueDate: string, isCampaign: boolean) => eventId,
        (state: CommonRootState, eventId, eventDueDate: string, isCampaign: boolean) => eventDueDate,
        (state: CommonRootState, eventId, eventDueDate: string, isCampaign: boolean) => isCampaign,
    ],
    (userMessageEntities, campaignEntities, eventId, eventDueDate, isCampaign) => {
        let event
        if (isCampaign) {
            event = campaignsSelectOneObjectByQuery(campaignEntities, { _id: eventId });
        } else {
            event = userMessagesSelectOneObjectByQuery(userMessageEntities, { _id: eventId });
        }

        if (!event) return undefined;

        if (isCampaign) {
            const campaignEvent = event as Campaign;
            
            return {
                _id: campaignEvent._id,
                campaignStatus: campaignEvent.campaignStatus, // For type detection
                template: campaignEvent.template ? {
                    asset: campaignEvent.template.asset // For asset preview
                } : undefined,
                dueDate: eventDueDate,
                message: campaignEvent.template?.message,
                messageColor: 'var(--calendar-color-sage)' // Direct campaign color
            };
        } else {
            const userMessage = event as UserMessage;

            // Use the shared color function based on message id
            const eventColor = getCalendarEventColor(userMessage._id);
            
            return {
                _id: userMessage._id,
                contactList: userMessage.contactList, // For contact avatar
                messageColor: eventColor, // Use the shared color function
                asset: userMessage.asset, // For asset preview
                dueDate: eventDueDate,
                message: userMessage.message,
                poll: userMessage.poll,
                isPoll: userMessage.isPoll,
                isRecurring: userMessage.isRecurring,
                rruleset: userMessage.rruleset
            };
        }
    }
);

export const getIsCustomMonthEventHighlighted = createSelector(
    [
        (state: CommonRootState, contactWaId) => state,
        (state: CommonRootState, contactWaId) => contactWaId,
    ],
    (state: CommonRootState, contactWaId) => {
        const highlightedContactId = state.WhatsAppReducer.highlightedContactId
        const isHighlighted = highlightedContactId && contactWaId === highlightedContactId;
        return isHighlighted
    }
)

// Optimized hover check - extracts only necessary state to minimize selector recalculations
export const getIsDayCellHovered = createSelector(
    [
        (state: CommonRootState) => state.WhatsAppReducer.hoveredDate,
        (state: CommonRootState, date) => date,
    ],
    (hoveredDate, date) => {
        if (!hoveredDate) return false;

        // Use getTime() for efficient date comparison instead of moment formatting
        const hoveredTime = new Date(hoveredDate).setHours(0, 0, 0, 0);
        const cellTime = new Date(date).setHours(0, 0, 0, 0);

        return hoveredTime === cellTime;
    }
)


interface AgendaDayGroup {
    date: string;
    messages: UserMessage[];
}

export const getAgendaFlattenedData = createSelector(
    [
        (state: CommonRootState) => state,
        (state: CommonRootState) => state.CalendarReducer.currentDate,
    ],
    (state: CommonRootState, currentDate) => {
        const events = getCalendarEvents(state);

        // Filter events for current month.
        // startOf/endOf month bounds make numeric comparison equivalent to isBetween(..., 'day', '[]')
        const currentMonthStartMs = moment(currentDate).startOf('month').valueOf();
        const currentMonthEndMs = moment(currentDate).endOf('month').valueOf();

        // Precompute each event's due timestamp once (instead of moment() per filter/sort comparison)
        const eventsWithTime = events.map(event => ({
            event,
            time: moment((event as UserMessage)?.dueDate ?? (event as Campaign)?.schedule?.dueDate).valueOf(),
        }));

        const sortedEvents = eventsWithTime
            .filter(({ time }) => time >= currentMonthStartMs && time <= currentMonthEndMs)
            .sort((a, b) => a.time - b.time);

        const groups: AgendaDayGroup[] = [];
        const groupsByDate = new Map<string, AgendaDayGroup>();

        sortedEvents.forEach(({ event, time }) => {
            const dateStr = moment(time).format('YYYY-MM-DD');
            let group = groupsByDate.get(dateStr);

            if (!group) {
                group = { date: dateStr, messages: [] };
                groupsByDate.set(dateStr, group);
                groups.push(group);
            }

            group.messages.push(event as UserMessage);
        });

        return groups.reduce((acc, group) => {
            acc.push({ type: 'header', date: group.date });
            group.messages.forEach(message => {
                acc.push({ type: 'message', message });
            });
            return acc;
        }, [] as Array<{ type: 'header'; date: string; } | { type: 'message'; message: UserMessage; }>);
    }
)